import { defineMessages } from "./define.js";

/**
 * The settings screen: members, the invite, appearance, notifications.
 *
 * The notification panel is the biggest block of words in the app, and most of
 * it exists to explain platform dead ends (SPEC §4.7), so the English is written
 * to say the same thing rather than to match the Russian word for word.
 */
export const settings = defineMessages("settings", {
  title: { ru: "Настройки", en: "Settings" },

  /* ------------------------------------------------------------- profile */

  profile: { ru: "Профиль", en: "Profile" },
  profileNote: {
    ru: "Имя и фотографию видят все, с кем вы в одном пространстве.",
    en: "Everyone you share a space with sees your name and photo.",
  },
  profileName: { ru: "Ваше имя", en: "Your name" },
  profileNameEmpty: { ru: "Имя не может быть пустым", en: "A name cannot be empty" },
  /**
   * The save is a tick beside the field now, so these say *what* is being
   * saved: on a button with no words on it, «Сохранить» alone is the whole
   * announcement a screen reader gets.
   */
  profileSave: { ru: "Сохранить имя", en: "Save the name" },
  profileSaving: { ru: "Сохраняем имя…", en: "Saving the name…" },
  profileSaved: { ru: "Сохранено", en: "Saved" },
  profileSaveFailed: { ru: "Не удалось сохранить", en: "Could not save" },
  /** The names of the picture button, which is the picture itself. */
  profilePhotoChoose: { ru: "Выбрать фото", en: "Choose a photo" },
  profilePhotoReplace: { ru: "Заменить фото", en: "Replace the photo" },
  // «Убрать» стояло рядом с «Заменить фото» и объяснялось соседом; теперь
  // кнопка одна и рядом с текстом о другом — приходится называть предмет.
  profilePhotoRemove: { ru: "Убрать фото", en: "Remove the photo" },
  profilePhotoUploading: { ru: "Загружаем фото…", en: "Uploading the photo…" },
  profilePhotoUnsupported: {
    ru: "Такой файл загрузить нельзя — нужна картинка",
    en: "That file cannot be uploaded — a picture is needed",
  },
  profilePhotoTooLarge: {
    ru: "Файл больше {size} МБ",
    en: "The file is larger than {size} MB",
  },
  profilePhotoFailed: {
    ru: "Не удалось загрузить фотографию",
    en: "Could not upload the photo",
  },
  /** On the picture itself, for a screen reader that cannot see whose it is. */
  profilePhotoOf: { ru: "Фотография: {name}", en: "Photo of {name}" },

  /* ------------------------------------------------------------- members */

  members: { ru: "Участники", en: "Members" },
  itsYou: { ru: "— это вы", en: "— that's you" },
  joined: { ru: "с {date}", en: "since {date}" },
  removeMember: { ru: "Исключить", en: "Remove" },
  removeMemberOf: {
    ru: "Исключить {name} из пространства",
    en: "Remove {name} from the space",
  },
  removeMemberTitle: { ru: "Исключить {name}?", en: "Remove {name}?" },
  removeMemberWhat: {
    ru: "{name} потеряет доступ к этому пространству — спискам, календарю, переписке. Написанное останется на месте.",
    en: "{name} loses access to this space — the lists, the calendar, the conversation. What they wrote stays.",
  },
  removeMemberBack: {
    ru: "Вернуть можно только новым приглашением.",
    en: "The only way back is a fresh invitation.",
  },
  removingMember: { ru: "Исключаем…", en: "Removing…" },
  removeMemberFailed: { ru: "Не вышло исключить", en: "Could not remove them" },

  /* -------------------------------------------------------------- invite */

  invite: { ru: "Приглашение", en: "Invite" },
  inviteMeta: { ru: "действует 7 дней", en: "valid for 7 days" },
  inviteNote: {
    ru: "Регистрация только по коду. Открытой формы нет.",
    en: "By code only. There is no open sign-up.",
  },

  /** The two doors, asked as one question. */
  inviteWhich: { ru: "Какое приглашение", en: "Which invitation" },

  /**
   * Two kinds of code, and every word here exists to keep them apart.
   *
   * Each one says where the person ends up, not what the code is called: «в
   * пространство «Дом»» against «в приложение», and under it one sentence about
   * what that person will see. Confusing the two means letting a stranger into
   * the room where the lists and the chat are, so neither wording leans on the
   * other being read first.
   *
   * Two names for the same door, because they are read in two places. The short
   * one is on the pill, which has to survive a 320px phone beside its twin; the
   * long one names the space and is the pill's accessible name, where a reader
   * announcing two near-identical options most needs the difference. The short
   * one is contained in the long one, so voice control still works on what is
   * visible. The space's name is in the consequence line too, which is where
   * somebody reading rather than skimming will meet it.
   */
  inviteSpaceShort: { ru: "В пространство", en: "Into this space" },
  inviteSpaceTitle: { ru: "В пространство «{space}»", en: "Into “{space}”" },
  inviteSpaceWhat: {
    ru: "Человек войдёт в «{space}»: те же списки, календарь, желания и переписка, что видите вы.",
    en: "They come into “{space}”: the same lists, calendar, wishes and chat you see.",
  },
  inviteSpaceCreate: { ru: "Код в пространство", en: "Space code" },
  inviteSpaceReissue: { ru: "Новый код в пространство", en: "New space code" },
  /** Shown to a member instead of a disabled button with no explanation. */
  inviteSpaceOwnerOnly: {
    ru: "Звать сюда может только владелец пространства.",
    en: "Only the owner of this space can invite into it.",
  },

  inviteAppTitle: { ru: "В приложение", en: "Into the app" },
  inviteAppWhat: {
    ru: "Человек заведёт своё пространство. Сюда он не попадёт и ничего вашего не увидит.",
    en: "They get a space of their own. They never enter this one and see nothing of yours.",
  },
  inviteAppCreate: { ru: "Код в приложение", en: "App code" },
  inviteAppReissue: { ru: "Новый код в приложение", en: "New app code" },

  inviteMinting: { ru: "Создаём…", en: "Creating…" },
  inviteFailed: { ru: "Не удалось создать код", en: "Could not create a code" },

  /**
   * The invitation as it will actually be sent.
   *
   * A bare link is not a message. Whoever hands one of these over was writing
   * the sentence around it by hand every time — "это в общие списки", "открой,
   * заведёшь своё" — so the sentence is written here instead, once, and the
   * button hands over the finished thing.
   *
   * Four lines, in the order the reader needs them: what this is and where it
   * leads, the link, the code on its own (it gets dictated aloud, and a code
   * buried inside a URL cannot be), and when it stops working. The two kinds
   * differ in the first line only, because that is the whole difference — one
   * says which room you are being let into, the other says you will get one of
   * your own.
   */
  inviteShareSpaceTitle: {
    ru: "Приглашение в «{space}»",
    en: "An invitation into “{space}”",
  },
  inviteShareSpaceText: {
    ru: "Зову вас в пространство «{space}» в Vmeste — общие списки, календарь, желания и переписка.\nСсылка: {link}\nКод: {code}\nПриглашение действует до {until}.",
    en: "I'm inviting you into “{space}” on Vmeste — our shared lists, calendar, wishes and chat.\nLink: {link}\nCode: {code}\nThe invitation works until {until}.",
  },
  inviteShareAppTitle: {
    ru: "Приглашение в Vmeste",
    en: "An invitation to Vmeste",
  },
  inviteShareAppText: {
    ru: "Приглашаю вас в Vmeste — общие списки, календарь, желания и переписка для своих. Вы заведёте своё пространство.\nСсылка: {link}\nКод: {code}\nПриглашение действует до {until}.",
    en: "I'm inviting you to Vmeste — shared lists, calendar, wishes and chat for the people close to you. You'll get a space of your own.\nLink: {link}\nCode: {code}\nThe invitation works until {until}.",
  },

  /**
   * One button, two names, because it does two different things.
   *
   * Where the system sheet exists (a phone, which is where invitations are
   * actually sent from) it opens it; where it does not (a desktop browser) it
   * puts the same text on the clipboard. Saying "Поделиться" on a machine that
   * is about to silently copy would be a small lie, so it says what it did.
   */
  inviteShare: { ru: "Поделиться", en: "Share" },
  inviteCopyMessage: { ru: "Скопировать текст", en: "Copy the message" },

  /**
   * Both options carry the same two buttons, so nothing here may be named by
   * the verb alone: a screen reader would announce «Поделиться» twice and the
   * one that opens a stranger's way into this space would sound like the one
   * that does not.
   */
  inviteSpaceShareLabel: {
    ru: "Поделиться приглашением в «{space}»",
    en: "Share the invitation into “{space}”",
  },
  inviteSpaceCopyLabel: {
    ru: "Скопировать приглашение в «{space}»",
    en: "Copy the invitation into “{space}”",
  },
  inviteSpaceCodeCopy: {
    ru: "Скопировать код в «{space}»",
    en: "Copy the code into “{space}”",
  },
  inviteAppShareLabel: {
    ru: "Поделиться приглашением в приложение",
    en: "Share the invitation into the app",
  },
  inviteAppCopyLabel: {
    ru: "Скопировать приглашение в приложение",
    en: "Copy the invitation into the app",
  },
  inviteAppCodeCopy: {
    ru: "Скопировать код в приложение",
    en: "Copy the app code",
  },

  /**
   * The same invitation, shown instead of sent.
   *
   * Two ways out of one block, and the words have to say why there are two. The
   * message is forwarded through somebody else's chat, so it never carries the
   * key. The square is held up to the other phone, never leaves the room, and
   * where this device has the key it carries that too — which is the only
   * reason the two are worded apart at all.
   *
   * These two are now the label of an icon button rather than words on it: the
   * square shares a row with «Поделиться», and only one of the two needs to
   * spell itself out. Nothing is lost to a screen reader, which reads the label.
   */
  inviteQrShow: { ru: "Показать квадрат", en: "Show the square" },
  inviteQrHide: { ru: "Убрать квадрат", en: "Take the square away" },
  inviteQrAim: {
    ru: "Наведите камеру того телефона, который зовёте",
    en: "Point the camera of the phone you are inviting",
  },
  inviteQrPlain: {
    ru: "Одно сканирование — и человек окажется на регистрации с уже вписанным кодом.",
    en: "One scan and they land on the sign-up screen with the code already filled in.",
  },
  inviteQrKeyed: {
    ru: "В квадрате и приглашение, и ключ: одно сканирование и впустит человека, и откроет ему пространство.",
    en: "The square holds the invitation and the key: one scan both lets them in and unlocks the space for them.",
  },
  inviteQrNoKey: {
    ru: "Ключа на этом устройстве нет, поэтому в квадрате только приглашение: человек войдёт, но записи останутся нечитаемыми, пока он не откроет пространство кодом восстановления.",
    en: "This device holds no key, so the square carries the invitation alone: they get in, but everything stays unreadable until they open the space with the recovery code.",
  },

  inviteCopiedMessage: { ru: "Скопировано", en: "Copied" },
  inviteCopiedCode: { ru: "Код скопирован", en: "Code copied" },
  inviteCopyManually: { ru: "Скопируйте вручную", en: "Copy this by hand" },

  /* ---------------------------------------------------------- appearance */

  appearance: { ru: "Оформление", en: "Appearance" },
  themeSystem: { ru: "Как в системе", en: "Match the system" },
  themeLight: { ru: "Светлая", en: "Light" },
  themeDark: { ru: "Тёмная", en: "Dark" },
  language: { ru: "Язык", en: "Language" },
  /** The language picker's third option; the two languages name themselves. */
  localeSystem: { ru: "Как в системе", en: "Match the system" },

  /* ------------------------------------------------------------ activity */

  activity: { ru: "Что происходило", en: "What happened" },
  activityMeta: { ru: "Лента изменений", en: "Change feed" },

  /* ------------------------------------------------------------- install */

  installTitle: { ru: "Поставить на домашний экран", en: "Add to the home screen" },
  installIos: {
    ru: '«Поделиться» → «На экран "Домой"». Пуши работают только у установленной версии.',
    en: 'Share → "Add to Home Screen". Push only works in the installed app.',
  },
  installAndroid: {
    ru: "меню Chrome → «Установить приложение».",
    en: 'Chrome menu → "Install app".',
  },
  installMac: {
    ru: "Safari → «Добавить в Dock».",
    en: 'Safari → "Add to Dock".',
  },

  /* ------------------------------------------------------------ security */

  /* Шифрование переехало в свой каталог — `i18n/messages/encryption.ts`. */

  signOut: { ru: "Выйти", en: "Sign out" },

  /* -------------------------------------------------------------- spaces */

  memberCount: {
    ru: {
      one: "{count} участник",
      few: "{count} участника",
      many: "{count} участников",
      other: "{count} участника",
    },
    en: { one: "{count} member", other: "{count} members" },
  },
  /* Название текущего пространства — карточка «Пространство». */

  // «Это», а не просто «Пространство»: их у человека может быть сколько угодно,
  // и карточка говорит про то, в котором он сейчас.
  space: { ru: "Это пространство", en: "This space" },
  spaceNameField: { ru: "Название пространства", en: "The name of this space" },
  spaceNameEmpty: {
    ru: "Название не может быть пустым",
    en: "A name cannot be empty",
  },
  // Как и в профиле: подпись у кнопки-галочки — единственное, что о ней
  // скажут вслух, поэтому она называет предмет, а не только действие.
  spaceSave: { ru: "Сохранить название", en: "Save the name" },
  spaceSaving: { ru: "Сохраняем название…", en: "Saving the name…" },
  spaceSaved: { ru: "Сохранено", en: "Saved" },
  spaceSaveFailed: {
    ru: "Не удалось сохранить название",
    en: "Could not save the name",
  },
  spaceNote: {
    ru: "Название видят все участники — оно же стоит наверху, в переключателе пространств.",
    en: "Everyone in the space sees this name — it is also the one in the switcher at the top.",
  },
  // Участнику поле не показывают вовсе: вместо выключенного поля — название и
  // строка о том, почему его здесь не меняют.
  spaceOwnerOnly: {
    ru: "Переименовать пространство может только его владелец.",
    en: "Only the owner of this space can rename it.",
  },
  spaceLocked: {
    ru: "Пространство зашифровано, а ключа на этом устройстве нет — изменить название отсюда нечем.",
    en: "The space is encrypted and this device has no key, so there is nothing to change the name with.",
  },

  /* Ещё одно пространство — карточка в настройках и пункт в переключателе. */

  spaceNew: { ru: "Новое пространство", en: "A new space" },
  spaceNewWhat: {
    ru: "Свои списки, календарь, чат и желания — отдельно от этого. Вы будете его владельцем.",
    en: "Its own lists, calendar, chat and wishes, apart from this one. You will be its owner.",
  },
  spaceNewName: {
    ru: "Название нового пространства",
    en: "The name of the new space",
  },
  spaceNewHint: {
    ru: "Можно не заполнять — тогда «{name}». Переименовать можно позже.",
    en: "Leave it blank for “{name}”. It can be renamed later.",
  },
  spaceNewCreate: { ru: "Создать пространство", en: "Create the space" },
  spaceNewCreating: { ru: "Создаём…", en: "Creating…" },
  spaceNewFailed: {
    ru: "Не удалось создать пространство",
    en: "Could not create the space",
  },
  spaceNewDone: {
    ru: "Готово — вы в пространстве «{space}»",
    en: "Done — you are now in “{space}”",
  },

  /* Удаление пространства — пункт в переключателе и диалог поверх всего.

     Слов здесь больше, чем где бы то ни было в настройках, и это не
     многословие: человек нажимает «удалить», думая про пустое пространство,
     которое завёл по ошибке, а нажать может, стоя в том, где четыре года
     переписки. Единственное, что отличает одно от другого, — эти строки. */

  spaceDelete: { ru: "Удалить пространство", en: "Delete this space" },
  spaceDeleteTitle: { ru: "Удалить «{space}»?", en: "Delete “{space}”?" },
  spaceDeleteWhat: {
    ru: "Исчезнут все списки, календарь, желания, переписка и вложенные файлы этого пространства.",
    en: "Every list, the calendar, the wishes, the chat and every file attached to them go with it.",
  },
  // Не «действие необратимо»: у нас нет ни корзины, ни бэкапа, из которого
  // человек мог бы что-то достать сам, и сказать это надо словами, а не
  // канцелярской формулой.
  spaceDeleteForever: {
    ru: "Навсегда. Ни отмены, ни корзины, ни способа восстановить это позже нет.",
    en: "For good. There is no undo, no bin, and no way to bring any of it back later.",
  },
  // Второй участник узнаёт об этом, только открыв приложение и не найдя
  // пространства. Тот, кто удаляет, должен понимать, что решает не за себя.
  spaceDeleteOthers: {
    ru: {
      one: "Это общее пространство: у второго участника оно тоже пропадёт, и он узнает об этом, только открыв приложение.",
      few: "Это общее пространство: у остальных {count} участника оно тоже пропадёт, и они узнают об этом, только открыв приложение.",
      many: "Это общее пространство: у остальных {count} участников оно тоже пропадёт, и они узнают об этом, только открыв приложение.",
      other: "Это общее пространство: у остальных {count} участника оно тоже пропадёт, и они узнают об этом, только открыв приложение.",
    },
    en: {
      one: "This space is shared: it disappears for the other person too, and they will find out only when they next open the app.",
      other: "This space is shared: it disappears for the other {count} people too, and they will find out only when they next open the app.",
    },
  },
  spaceDeleteConfirmLabel: {
    ru: "Название пространства",
    en: "The name of the space",
  },
  spaceDeleteConfirmHint: {
    ru: "Напечатайте «{space}», чтобы подтвердить.",
    en: "Type “{space}” to confirm.",
  },
  spaceDeleteSubmit: { ru: "Удалить навсегда", en: "Delete for good" },
  spaceDeleting: { ru: "Удаляем…", en: "Deleting…" },
  spaceDeleteFailed: {
    ru: "Не удалось удалить пространство",
    en: "Could not delete the space",
  },
  // Отказ на последнем пространстве, объяснённый до нажатия, а не после.
  spaceDeleteLast: {
    ru: "Это ваше единственное пространство, а приложению нужно хотя бы одно — без него не открыть ни списки, ни календарь. Создайте другое, а это удалите после.",
    en: "This is your only space, and the app needs at least one — without it there are no lists and no calendar to open. Make another one first, then delete this.",
  },

  /* ------------------------------------------------- notifications: panel */

  notifTitle: { ru: "Уведомления", en: "Notifications" },
  notifLegend: { ru: "О чём уведомлять", en: "What to notify about" },
  notifPrefsLoading: { ru: "Загружаем настройки…", en: "Loading the settings…" },
  notifPrefsErrorHint: {
    ru: "Пока настройки не загрузились, мы не показываем переключатели — иначе они показали бы не то, что на самом деле стоит на сервере.",
    en: "Until the settings load we keep the switches hidden — they would show something other than what the server actually has.",
  },
  notifDevicesShared: {
    ru: "Настройки ниже общие для всех ваших устройств. На это устройство уведомления сейчас не приходят.",
    en: "The settings below are shared by all your devices. This device is not getting notifications right now.",
  },
  notifSaveError: {
    ru: "{message}. Изменение осталось несохранённым.",
    en: "{message}. The change was not saved.",
  },

  /* ------------------------------------------- notifications: this device */

  notifChecking: {
    ru: "Проверяем, может ли это устройство получать уведомления…",
    en: "Checking whether this device can receive notifications…",
  },
  notifUnsupportedTitle: {
    ru: "Этот браузер не умеет пуши",
    en: "This browser cannot do push",
  },
  notifUnsupportedBody: {
    ru: "Web Push здесь недоступен, и включить его нечем. Уведомления будут приходить на другое устройство, если подписаться там.",
    en: "Web Push is not available here, and there is nothing to switch on. Notifications will arrive on another device if you subscribe there.",
  },
  notifInstallTitle: {
    ru: "Сначала добавьте приложение на экран «Домой»",
    en: "Add the app to the Home Screen first",
  },
  notifInstallBody: {
    ru: "На iPhone и iPad уведомления работают только у приложения, установленного на домашний экран (iOS 16.4 и новее). В обычной вкладке их не выдаёт сама система — дело не в настройках.",
    en: "On iPhone and iPad notifications only work for an app added to the home screen (iOS 16.4 and later). In an ordinary tab the system itself withholds them — it is not a setting you can change.",
  },
  notifInstallStep1: {
    ru: "Нажмите «Поделиться» — квадрат со стрелкой вверх.",
    en: 'Tap "Share" — the square with an arrow out of it.',
  },
  notifInstallStep2: {
    ru: "Выберите «На экран „Домой“».",
    en: 'Choose "Add to Home Screen".',
  },
  notifInstallStep3: {
    ru: "Откройте Vmeste с домашнего экрана и вернитесь на этот экран.",
    en: "Open Vmeste from the home screen and come back to this screen.",
  },
  notifInstallNotSafari: {
    ru: "Сейчас открыт не Safari. Добавить на домашний экран умеет только Safari — откройте Vmeste в нём.",
    en: "This is not Safari. Only Safari can put an app on the home screen — open Vmeste there.",
  },
  notifDeniedTitle: { ru: "Уведомления запрещены", en: "Notifications are blocked" },
  notifDeniedBody: {
    ru: "Спросить ещё раз не получится — браузер запомнил отказ и больше не покажет окно. Разрешение меняется только в настройках сайта.",
    en: "Asking again will not work — the browser remembered the refusal and will not show the prompt. The permission changes only in the site settings.",
  },
  notifDeniedSafari: {
    ru: "«Настройки» → «Приложения» → Safari → «Уведомления».",
    en: 'Settings → Apps → Safari → "Notifications".',
  },
  notifDeniedChrome: {
    ru: "значок слева от адреса → «Уведомления».",
    en: 'the icon left of the address → "Notifications".',
  },
  notifOff: {
    ru: "На этом устройстве уведомления выключены.",
    en: "Notifications are off on this device.",
  },
  notifEnable: { ru: "Включить уведомления", en: "Turn notifications on" },
  notifEnabling: { ru: "Подключаем…", en: "Connecting…" },
  notifOn: {
    ru: "Уведомления включены на этом устройстве",
    en: "Notifications are on for this device",
  },
  notifOnHint: {
    ru: "Выключение здесь касается только этого устройства — на остальных подписка останется.",
    en: "Turning them off here touches this device only — the others stay subscribed.",
  },
  notifDisable: { ru: "Выключить на этом устройстве", en: "Turn off on this device" },
  notifDisabling: { ru: "Отключаем…", en: "Disconnecting…" },

  /* ------------------------------------------- notifications: what to send */

  typeMessageNew: { ru: "Новые сообщения", en: "New messages" },
  typeMessageNewHint: { ru: "В общем чате и в темах", en: "In the main chat and in threads" },
  typeItemAssigned: { ru: "Дело назначили на вас", en: "A task assigned to you" },
  typeItemAssignedHint: {
    ru: "Пункт списка с вами в исполнителях",
    en: "A list item with you among the doers",
  },
  typeItemDue: { ru: "Подходит срок", en: "A deadline is near" },
  typeItemDueHint: {
    ru: "Незадолго до срока по делу",
    en: "Shortly before a task is due",
  },
  typeEventSoon: { ru: "Скоро событие", en: "An event is coming up" },
  typeEventSoonHint: { ru: "Напоминание из календаря", en: "A reminder from the calendar" },
  typeWishAdded: { ru: "Новая хотелка", en: "A new wish" },
  typeWishAddedHint: {
    ru: "Кто-то добавил хотелку в пространство",
    en: "Someone added a wish to the space",
  },
  typePlaceAdded: { ru: "Новое место", en: "A new place" },
  typePlaceAddedHint: {
    ru: "Кто-то отметил место на карте",
    en: "Someone put a place on the map",
  },
  typeSpaceJoined: { ru: "Новый участник", en: "A new member" },
  typeSpaceJoinedHint: {
    ru: "Кто-то присоединился к пространству",
    en: "Someone joined the space",
  },

  /* ----------------------------------------- notifications: how far ahead */

  /**
   * One phrase per unit, and each already carries «за» / "before".
   *
   * Composing «за » + «15 минут» from two messages would be a trap in Russian:
   * the preposition takes the accusative, so «за 1 минуту» — while the same
   * phrase standing on its own is «1 минута». Whole phrases keep the grammar
   * where the words are, and the count still picks the form through
   * `Intl.PluralRules`, which is what gets 11–14 right.
   *
   * They spell the number out rather than saying «за час» because the stored
   * value is any integer, not only the presets on screen: another device — or a
   * future one of ours — can put 45 here, and the screen has to read it back.
   */
  leadMinutes: {
    ru: {
      one: "за {count} минуту",
      few: "за {count} минуты",
      many: "за {count} минут",
      other: "за {count} минут",
    },
    en: { one: "{count} minute before", other: "{count} minutes before" },
  },
  leadHours: {
    ru: {
      one: "за {count} час",
      few: "за {count} часа",
      many: "за {count} часов",
      other: "за {count} часов",
    },
    en: { one: "{count} hour before", other: "{count} hours before" },
  },
  leadDays: {
    ru: {
      one: "за {count} день",
      few: "за {count} дня",
      many: "за {count} дней",
      other: "за {count} дней",
    },
    en: { one: "{count} day before", other: "{count} days before" },
  },
  /** Zero. Not "unset" — «в момент» is a thing somebody chooses. */
  leadAtMoment: { ru: "в момент", en: "at the time" },
  /** Names the row of presets for a screen reader; the switch above says which. */
  leadEventLegend: {
    ru: "За сколько предупредить о событии",
    en: "How much notice before an event",
  },
  leadItemLegend: {
    ru: "За сколько предупредить о сроке",
    en: "How much notice before a deadline",
  },

  /* ------------------------------------------- notifications: quiet hours */

  quietTitle: { ru: "Тихие часы", en: "Quiet hours" },
  quietHint: {
    ru: "В этом промежутке уведомления не приходят. Окно через полночь — нормальный случай: «с 23:00 до 07:00» значит ночь, а не сутки.",
    en: 'Nothing arrives inside this window. A window across midnight is the normal case: "23:00 to 07:00" means the night, not the whole day.',
  },
  quietOff: {
    ru: "Уведомления приходят в любое время.",
    en: "Notifications arrive at any hour.",
  },
  quietFrom: { ru: "С", en: "From" },
  quietTo: { ru: "До", en: "To" },
  quietReset: { ru: "Сбросить", en: "Clear" },
  quietHalf: {
    ru: "Укажите обе границы — пока стоит только одна, тихие часы не включены.",
    en: "Set both ends — with only one of them, quiet hours are not on.",
  },
  quietWraps: {
    ru: "Промежуток проходит через полночь — так и задумано: тихо с {from} до {to} следующего дня.",
    en: "The window runs across midnight — that is the idea: quiet from {from} until {to} the next day.",
  },
  quietTimeZone: { ru: "Часовой пояс:", en: "Time zone:" },
  quietTimeZoneUnset: { ru: "не задан", en: "not set" },
  /**
   * The sentence is split because the timezone between the halves is rendered in
   * a monospace span; the two fragments are never used apart.
   */
  quietTimeZoneDiffers: { ru: "— это устройство сейчас в", en: "— this device is in" },
  quietTimeZoneDiffersTail: {
    ru: ", так что тихие часы наступят в другое время.",
    en: ", so quiet hours will land at a different time.",
  },
  quietAdoptTimeZone: { ru: "Считать по {zone}", en: "Use {zone}" },

  /* ---------------------------------------------- notifications: the save */

  saving: { ru: "Сохраняем…", en: "Saving…" },
  saved: { ru: "Сохранено", en: "Saved" },
  notSaved: { ru: "Не сохранено", en: "Not saved" },

  /* --------------------------------------------- notifications: the wires */

  pushNoConnection: { ru: "Нет связи с сервером", en: "No connection to the server" },
  pushNotReady: {
    ru: "Сервер пока не умеет уведомления",
    en: "The server does not do notifications yet",
  },
  pushServerError: {
    ru: "Сервер ответил ошибкой ({status})",
    en: "The server answered with an error ({status})",
  },
  pushBadResponse: {
    ru: "Сервер прислал неожиданный ответ",
    en: "The server sent an unexpected answer",
  },
  pushRefused: { ru: "Браузер отказал в подписке", en: "The browser refused the subscription" },
  pushAborted: { ru: "Подписка не завершилась", en: "The subscription never finished" },
  pushFailed: {
    ru: "Не удалось подписаться на уведомления",
    en: "Could not subscribe to notifications",
  },
  pushSwNotReadyReload: {
    ru: "Service worker ещё не готов — обновите страницу",
    en: "The service worker is not ready yet — reload the page",
  },
  pushSwNotReady: { ru: "Service worker не готов", en: "The service worker is not ready" },
  pushNoPermission: { ru: "Разрешение не выдано", en: "Permission was not granted" },
  pushBadSubscription: {
    ru: "Браузер выдал подписку в неожиданном виде",
    en: "The browser produced a subscription in an unexpected shape",
  },
  prefsLoadFailed: {
    ru: "Не удалось загрузить настройки",
    en: "Could not load the settings",
  },
  saveFailed: { ru: "Не удалось сохранить", en: "Could not save" },
});
